/**
 * TanStack Query hooks for employee management.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

import {
  changeEmployeeRole,
  deactivateEmployee,
  inviteEmployee,
  listEmployees,
} from './employeesApi';
import type { Employee, EmployeeRole, InviteEmployeeRequest } from './employeesApi';

export const EMPLOYEES_QUERY_KEY = ['employees'] as const;

export function useEmployees(options?: {
  enabled?: boolean;
}): UseQueryResult<Employee[], NormalizedApiError> {
  return useQuery<Employee[], NormalizedApiError>({
    queryKey: EMPLOYEES_QUERY_KEY,
    queryFn: listEmployees,
    enabled: options?.enabled ?? true,
  });
}

export function useInviteEmployee() {
  const queryClient = useQueryClient();
  return useMutation<Employee, NormalizedApiError, InviteEmployeeRequest>({
    mutationFn: inviteEmployee,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: EMPLOYEES_QUERY_KEY }),
  });
}

export function useChangeEmployeeRole() {
  const queryClient = useQueryClient();
  return useMutation<Employee, NormalizedApiError, { id: string; role: EmployeeRole }>({
    mutationFn: ({ id, role }) => changeEmployeeRole(id, role),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: EMPLOYEES_QUERY_KEY }),
  });
}

export function useDeactivateEmployee() {
  const queryClient = useQueryClient();
  return useMutation<void, NormalizedApiError, string>({
    mutationFn: deactivateEmployee,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: EMPLOYEES_QUERY_KEY }),
  });
}
